// @ts-check
import { gql } from '@apollo/client/core';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Image, View } from '@react-pdf/renderer';
import React from 'react';
import {
  Company,
  Image as BottlebooksImage,
  Page,
  Product,
  QRCode,
  // Stencil,
  Text,
  Title,
} from '../../pdf-design-system';
import {
  PagesProvider,
  usePage,
} from '../../pdf-design-system/Base/PageLayout/Page';
import sourceSansPro400 from '../../pdf-design-system/fonts/source-sans-pro/400';
import sourceSansPro500 from '../../pdf-design-system/fonts/source-sans-pro/500';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import messages from './locale/messages';

const productCountFirstPage = 3;

export default function ForumViniCatalog({
  data,
  locale,
  options = { bleed: 0 },
  ...rest
}) {
  const sections = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((section, index) => {
          const { registration } = section;
          return (
            <ExhibitorPage
              key={`first-${index}`}
              registration={registration}
              options={options}
              locale={locale}
            />
          );
        })}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

function CatalogPage({
  registration,
  theme,
  bleed = 15, // Rediculously high number so that we recognize when it isn't set correctly
  style = {},
  children,
  ...rest
}) {
  const { withBleed } = useBleed({ bleed });

  return (
    <Page
      size={{ width: withBleed(148, 2), height: withBleed(210, 2) }}
      style={[
        { paddingVertical: withBleed(0), paddingHorizontal: withBleed(0) },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {/* <Stencil
        src="https://res.cloudinary.com/bottlebooks/image/upload/v1658406260/pdf-objects/Bottlebooks/standard-catalog-stencil.png"
        style={{ height: '210mm', padding: `${bleed}mm` }}
      /> */}

      {children}
    </Page>
  );
}

/**
 * This contains only the producer content.
 * @param {*} param0
 * @returns
 */
function ExhibitorPage({ registration, options, locale, ...rest }) {
  return (
    <CatalogPage
      registration={registration}
      theme={theme}
      bleed={options.bleed}
      {...rest}
    >
      <ExhibitorContent
        registration={registration}
        options={options}
        locale={locale}
      />
    </CatalogPage>
  );
}

export function ExhibitorContent({ registration, options, locale, ...rest }) {
  const { withBleed } = useBleed(options);
  const firstPageProducts = registration?.registeredProducts?.nodes
    ?.filter((product) => product.includeInCatalog.value)
    .slice(0, productCountFirstPage);

  // Gourmet exhibitors have gourmet products but nothing else
  const isGourmetExhibitor =
    registration.gourmetOffering?.values?.length > 0 &&
    registration.wineOffering?.values?.length === 0 &&
    registration.servicesOffering?.values?.length === 0;
  return (
    <View {...rest}>
      <PageHeader
        theme={theme}
        registration={registration}
        style={{
          position: 'absolute',
          top: withBleed(7.5), // We need to add bleed here because of position: 'absolute'
          left: 0,
          right: 0,
        }}
        bleed={options.bleed}
      />
      <PageBody
        style={{
          marginTop: '69mm',
        }}
      >
        <View style={{ height: 319 }}>
          <ExhibitorHeader registration={registration} />
          <View style={{ height: '63mm', display: 'flex' }}>
            {firstPageProducts?.length > 0 ? (
              <View>
                <Text style={{ fontSize: 10.5, marginTop: 3 }}>
                  Unsere ausgewählten Produkte
                </Text>
                {firstPageProducts?.map((registeredProduct, index) => (
                  <ProductRow
                    key={registeredProduct.productId}
                    registeredProduct={registeredProduct}
                    index={index + 1}
                    locale={locale}
                  />
                ))}
              </View>
            ) : isGourmetExhibitor ? (
              <GourmetProducts registration={registration} />
            ) : (
              <EmptyState style={{ marginTop: 15 }} />
            )}
          </View>
        </View>
      </PageBody>
      <PageFooter registration={registration} />
    </View>
  );
}

function ProductIcon({ label, src, style }) {
  return (
    <View
      style={{
        display: 'flex',
        alignItems: 'center',
        marginHorizontal: 4,
        marginBottom: 10,
        marginTop: 5,
      }}
    >
      <Icon src={src} style={[{ width: 75, height: 60, margin: 0 }, style]} />
      <Text>{label}</Text>
    </View>
  );
}

function Icon({ src, style }) {
  return (
    <Image
      src={src}
      style={[
        { height: 15, width: 15, marginRight: 2, marginBottom: 2 },
        style,
      ]}
    />
  );
}
export function ServicesIcon({ style }) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Dienstleistungen.png"
      style={style}
    />
  );
}
export function GourmetIcon({ style }) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Sonstige-Feinkost.png"
      style={style}
    />
  );
}
export function WineIcon({ style }) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Wein.png"
      style={style}
    />
  );
}

// Gourmet categories

function WurstUndSchinken({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Wurst und Schinken"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Wurst-und-Schinkenspezialitaeten.png"
      style={style}
    />
  );
}
function Kaese({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Käse"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Kaese.png"
      style={style}
    />
  );
}
function Aufstriche({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Aufstriche"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Aufstriche-Konfituere.png"
      style={style}
    />
  );
}
function Spirits({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Spirituosen"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Spirituosen.png"
      style={style}
    />
  );
}
function OtherGourmet({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Sonstige Feinkost"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Sonstige-Feinkost.png"
      style={style}
    />
  );
}
function Oil({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Essige / Öle"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993936/pdf-objects/Meininger/FV22_Icons_Essig-Olee.png"
      style={style}
    />
  );
}
function Schokolade({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Schokolade"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Schokolade.png"
      style={style}
    />
  );
}
function Oliven({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Oliven"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Olive.png"
      style={style}
    />
  );
}
function Bier({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Biere"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Bierspezialitaeten.png"
      style={style}
    />
  );
}
function Kaffee({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Kaffee"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993937/pdf-objects/Meininger/FV22_Icons_Kaffee.png"
      style={style}
    />
  );
}
function Delikatessen({ style = {} } = {}) {
  return (
    <ProductIcon
      label="Delikatessen"
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658993936/pdf-objects/Meininger/FV22_Icons_Delikatessen.png"
      style={style}
    />
  );
}

function PageHeader({ bleed, theme, registration, style = {} }) {
  const { withBleed } = useBleed({ bleed });
  return (
    <View
      style={[
        {
          backgroundColor: theme.colors.primary,
          paddingRight: withBleed(15),
          paddingLeft: withBleed(15),
          position: 'relative',
        },
        style,
      ]}
    >
      {registration.profile?.mainImage?.fixed?.src && (
        <Image
          src={registration.profile?.mainImage?.fixed?.src}
          // Ensure images with transparent background render are visible
          style={{ backgroundColor: 'white' }}
        />
      )}
    </View>
  );
}

export function PageBody({ children, style = {}, ...rest }) {
  return (
    <View
      style={[
        {
          paddingHorizontal: '15mm',
        },
        style,
      ]}
      {...rest}
    >
      {children}
    </View>
  );
}

function PageFooter({ registration, ...rest }) {
  const { alignment } = usePage();
  return (
    <View style={{ display: 'flex', paddingHorizontal: '15mm' }} {...rest}>
      <View
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexDirection: 'row',
          height: 40,
        }}
      >
        {registration.collection?.site?.publicUrl &&
          registration.collection?.site?.publicUrl?.trim() !== '' && (
            <React.Fragment>
              <QRCode
                url={`${registration.collection?.site?.publicUrl}/e/${registration.companyId}`}
                errorCorrectionLevel="L"
                margin={2}
                style={{
                  width: '15mm',
                  height: '15mm',

                  // ...(alignment === 'left'
                  //   ? { right: withBleed(18) }
                  //   : { left: withBleed(18) }),
                }}
              />
              <View
                style={{
                  marginLeft: '1mm',
                  marginRight: '2mm',
                  paddingRight: '2mm',
                  borderRight: '1px solid #ccc',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Text variant="small" style={{ width: 50 }}>
                  Scannen für persönliche Notizen
                </Text>
              </View>
            </React.Fragment>
          )}
        <View style={{ flexGrow: 1 }}>
          <Text variant="small">{registration.profile?.website}</Text>
          <Text variant="small">{registration.profile?.email}</Text>
          <Text variant="small">{registration.profile?.telephone}</Text>
        </View>
      </View>
      <View
        style={{
          borderTop: '1px solid #CCC',
          marginTop: 8,
          paddingTop: 3,
          display: 'flex',
          flexDirection: alignment === 'right' ? 'row' : 'row-reverse',
          justifyContent: 'flex-end',
        }}
      >
        <Text variant="small" style={{ marginRight: 5 }}>
          {registration.collection.name}
        </Text>
        <BottlebooksImage
          src={registration.collection?.logo?.url}
          height={15}
          width={70}
          trim
        />
      </View>
    </View>
  );
}

/**
 * @param {object} props
 * @param {number} props.number
 * @param {string} props.number
 */
function formatNumber({ locale = 'en', number }) {
  if (number == null) return '';
  return locale === 'en'
    ? number.toFixed(1).replace(',', '.')
    : number.toFixed(1).replace('.', ',');
}

function ExhibitorHeader({ registration, ...rest }) {
  return (
    <View {...rest}>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          marginBottom: 3,
          marginLeft: '3mm',
        }}
      >
        <View
          style={{
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #ccc',
            padding: 5,
            marginTop: -35,
            marginRight: '3mm',
            display: 'flex',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Company.ProfileLogo
            companyProfile={{
              logo: registration.profile.autoSizedLogo,
            }}
            height={55}
            width={70}
          />
        </View>
        <View style={{ display: 'flex' }}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Text variant="light" style={{ marginRight: '0.5mm' }}>
              {registration.stand?.name || 'Stand XYZ '}
            </Text>
            {registration.wineOffering?.values?.length > 0 && (
              <WineIcon style={{ marginRight: '0.5mm' }} />
            )}
            {registration.gourmetOffering?.values?.length > 0 && (
              <GourmetIcon style={{ marginRight: '1mm' }} />
            )}
            {registration.servicesOffering?.values?.length > 0 && (
              <ServicesIcon style={{ marginRight: '0.5mm' }} />
            )}
          </View>
          <Title style={{ width: 292, paddingRight: '2cm', height: 21 }}>
            {registration.profile.name}
          </Title>
          <Text variant="light">
            {registration.registeredProducts?.nodes?.[0]?.product
              .denomination || ' '}
          </Text>
        </View>
      </View>
      <View
        style={{
          borderBottom: '1px solid #CCC',
          paddingVertical: 5,
          maxHeight: 92,
        }}
      >
        <Text variant="light" style={{ fontSize: 9 }}>
          {registration.profile.shortDescription}
        </Text>
      </View>
    </View>
  );
}

function EmptyState({ style }) {
  return (
    <View
      style={[
        {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        style,
      ]}
    >
      <Image
        src="https://res.cloudinary.com/bottlebooks/image/upload/v1658819285/pdf-objects/Meininger/produkteAmStand.png"
        style={{ height: 150 }}
      />
    </View>
  );
}

function GourmetProducts({ registration }) {
  const gourmetOfferings = (registration.gourmetOffering?.values || [])?.map(
    ({ key }) => key
  );
  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        marginTop: '1mm',
        flexWrap: 'wrap',
      }}
    >
      {gourmetOfferings.includes('aufstricheKonfituren') && <Aufstriche />}
      {gourmetOfferings.includes('bierspezialitaten') && <Bier />}
      {gourmetOfferings.includes('delikatessen') && <Delikatessen />}
      {gourmetOfferings.includes('essigeOle') && <Oil />}
      {gourmetOfferings.includes('kaffee') && <Kaffee />}
      {gourmetOfferings.includes('kase') && <Kaese />}
      {gourmetOfferings.includes('olive') && <Oliven />}
      {gourmetOfferings.includes('schokolade') && <Schokolade />}
      {gourmetOfferings.includes('spirituosen') && <Spirits />}
      {gourmetOfferings.includes('wurstUndSchinkenspezialitaten') && (
        <WurstUndSchinken />
      )}
      {gourmetOfferings.includes('sonstigeFeinkost') && <OtherGourmet />}
    </View>
  );
}

function ProductRow({ registeredProduct, index, locale }) {
  const { i18n } = useLingui();
  const { alcohol } = registeredProduct.product;
  const analytics = [
    alcohol && i18n._(t`Alc.: ${formatNumber({ locale, number: alcohol })}%`),
  ]
    .filter(Boolean)
    .join(' | ');
  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '20mm',
        flexShrink: 0,
        paddingTop: '3mm',
        justifyContent: 'space-between',
      }}
    >
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          paddingRight: '2mm',
          width: 300,
        }}
      >
        <View style={{ width: '6mm' }}>
          <Text style={{ fontSize: 10.5 }}>{index}.</Text>
        </View>
        <View>
          <Text style={{ fontSize: 10.5 }}>
            {registeredProduct.product.name}
          </Text>
          <Text variant="light">{registeredProduct.product.denomination}</Text>
          <Text variant="light">{analytics}</Text>
        </View>
      </View>
      <View
        style={{
          width: 64,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Product.BottleImage
          product={registeredProduct.product}
          height={90}
          style={{ transform: 'rotate(-90deg)' }}
        />
      </View>
    </View>
  );
}

export function useBleed({ bleed = 15 } = { bleed: 15 }) {
  /**
   * @param {number} measurementInMm
   * @returns
   */
  function withBleed(measurementInMm, factor = 1) {
    return `${measurementInMm + bleed * factor}mm`;
  }
  return { withBleed };
}

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

const colors = {
  primary: '#911845',
  accent: '#ffd101',
};

/** @type {Theme} */
export const theme = {
  colors,
  components: {
    Text: {
      base: { ...sourceSansPro400, fontSize: '10pt' },
      small: { ...sourceSansPro400, fontSize: '9pt' },
      bold: { ...sourceSansPro500 },
      light: { color: '#555' },
    },
    Title: {
      base: { ...sourceSansPro400 },
      small: {
        fontSize: 12,
      },
      default: {
        fontSize: '16pt',
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

ForumViniCatalog.fragment = gql`
  fragment ForumViniCatalog on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...ForumViniCatalog_Table
    }
  }

  fragment ForumViniCatalog_Table on RegistrationCatalogSection {
    registration {
      registrationId
      companyId
      stand {
        name
      }

      wineOffering: customFieldValue(key: "weinangebot") {
        ... on MultiSelectFieldValue {
          values {
            key
          }
        }
      }
      gourmetOffering: customFieldValue(
        key: "angebotFeinkostUndSpezialitaten"
      ) {
        ... on MultiSelectFieldValue {
          values {
            key
          }
        }
      }
      servicesOffering: customFieldValue(
        key: "angebotDienstleistungenUndZubehor"
      ) {
        ... on MultiSelectFieldValue {
          values {
            key
          }
        }
      }

      collection {
        collectionId
        eventId: collectionId
        name
        site {
          publicUrl
        }
        logo {
          url
          fixed(width: 100, height: 20) {
            src
          }
        }
      }

      profile {
        name
        street
        postalCode
        city
        country
        telephone
        email
        website
        shortDescription
        ...ProfileLogo
        mainImage {
          fixed(width: 500, height: 255, gravity: FACES) {
            src
          }
        }
        autoSizedLogo: logo {
          url
        }
      }

      registeredProducts {
        nodes {
          includeInCatalog: customFieldValue(key: "includeInCatalog") {
            ... on YesNoFieldValue {
              value
            }
          }
          producer {
            isSeekingDistribution
            distributionContacts {
              contactId
              companyName
            }
          }
          pricing {
            currency
            priceType
            price
          }
          productId
          product {
            productId
            name
            alcohol

            regionHierarchy
            denomination
            ...Blend
            ...BottleImage
            ... on Wine {
              vineyard
              designation
              residualSugar
              totalAcidity
            }
          }
        }
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
  ${Company.ProfileLogo.fragment}
`;

export function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections?.filter(
    (section) => section.__typename === 'RegistrationCatalogSection'
  );
}
